import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertTriangle, AlertCircle, Info, Globe, FileText, BarChart3, Clock, CheckCircle2, Wrench } from 'lucide-react'
import { issueDescriptions } from '../crawl/data/issueDescriptions'
import { scoreColor } from '@/client/lib/colors'

interface ReportMeta {
  id: string
  domain: string
  status: string
  pages_crawled: number
  completed_at: string | null
  avg_seo_score: number | null
}

interface ReportOverview {
  total_pages: number
  status_2xx: number
  status_3xx: number
  status_4xx: number
  status_5xx: number
  missing_titles: number
  missing_descriptions: number
  avg_seo_score: number | null
  avg_response_time: number | null
}

interface ReportIssue {
  type: string
  label: string
  count: number
  severity: 'error' | 'warning' | 'info'
}

interface ScoreDistribution {
  good: number
  ok: number
  poor: number
}

interface WorstPage {
  url: string
  seo_score: number
  title: string | null
  status_code: number
  word_count: number | null
}

interface PriorityAction {
  type: string
  label: string
  count: number
  severity: string
  priority: number
  howToFix: string | null
  description: string | null
}

async function fetchReport<T>(path: string): Promise<T> {
  const res = await fetch(path)
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error((data as { error?: string }).error || `Request failed: ${res.status}`)
  }
  return res.json()
}

export function ReportPage() {
  const { token } = useParams<{ token: string }>()

  // Check if this token has a config_id — if so, render the modular HTML report via iframe
  const { data: tokenInfo } = useQuery({
    queryKey: ['report', token, 'token-info'],
    queryFn: async () => {
      const res = await fetch(`/api/reports/${token}`)
      if (!res.ok) throw new Error('Report not found')
      return res.json() as Promise<ReportMeta & { config_id?: string }>
    },
    enabled: !!token,
  })

  // All hooks must be called before any conditional return (React rules of hooks).
  // These queries only fire for legacy reports (no config_id).
  const isLegacyReport = !!token && !tokenInfo?.config_id

  const { data: meta, isLoading: metaLoading, error: metaError } = useQuery({
    queryKey: ['report', token, 'meta'],
    queryFn: () => fetchReport<ReportMeta>(`/api/reports/${token}`),
    enabled: isLegacyReport,
  })

  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['report', token, 'overview'],
    queryFn: () => fetchReport<ReportOverview>(`/api/reports/${token}/overview`),
    enabled: isLegacyReport && !!meta,
  })

  const { data: issuesData, isLoading: issuesLoading } = useQuery({
    queryKey: ['report', token, 'issues'],
    queryFn: () => fetchReport<{ issues: ReportIssue[] }>(`/api/reports/${token}/issues`),
    enabled: isLegacyReport && !!meta,
  })

  const { data: scoreDist } = useQuery({
    queryKey: ['report', token, 'score-distribution'],
    queryFn: () => fetchReport<ScoreDistribution>(`/api/reports/${token}/score-distribution`),
    enabled: isLegacyReport && !!meta,
  })

  const { data: worstPagesData } = useQuery({
    queryKey: ['report', token, 'worst-pages'],
    queryFn: () => fetchReport<{ pages: WorstPage[] }>(`/api/reports/${token}/worst-pages`),
    enabled: isLegacyReport && !!meta,
  })

  const { data: actionsData } = useQuery({
    queryKey: ['report', token, 'priority-actions'],
    queryFn: () => fetchReport<{ actions: PriorityAction[] }>(`/api/reports/${token}/priority-actions`),
    enabled: isLegacyReport && !!meta,
  })

  // Modular HTML report — render in iframe
  if (tokenInfo && tokenInfo.config_id) {
    return (
      <div className="min-h-screen bg-background">
        <div className="border-b bg-card">
          <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Globe className="h-5 w-5 text-primary" />
              <span className="font-semibold">{tokenInfo.domain}</span>
            </div>
            <a
              href={`/api/reports/${token}/html`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary hover:underline"
            >
              Download PDF
            </a>
          </div>
        </div>
        <iframe
          src={`/api/reports/${token}/html`}
          className="w-full border-0"
          style={{ minHeight: 'calc(100vh - 56px)' }}
          title={`SEO Report — ${tokenInfo.domain}`}
        />
      </div>
    )
  }

  if (metaLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="max-w-4xl mx-auto space-y-4">
          <Skeleton className="h-12 w-64" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    )
  }

  if (metaError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Report Not Available</h2>
            <p className="text-muted-foreground">{(metaError as Error).message}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!meta) return null

  const errors = issuesData?.issues.filter((i) => i.severity === 'error') ?? []
  const warnings = issuesData?.issues.filter((i) => i.severity === 'warning') ?? []
  const infos = issuesData?.issues.filter((i) => i.severity === 'info') ?? []

  const severityIcon = (severity: string) => {
    if (severity === 'error') return <AlertCircle className="h-4 w-4 text-destructive" />
    if (severity === 'warning') return <AlertTriangle className="h-4 w-4 text-yellow-500" />
    return <Info className="h-4 w-4 text-blue-500" />
  }

  const reportScoreColor = (score: number | null) => {
    if (!score) return 'text-muted-foreground'
    return scoreColor(score)
  }

  const totalScored = (scoreDist?.good ?? 0) + (scoreDist?.ok ?? 0) + (scoreDist?.poor ?? 0)

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center gap-3 mb-2">
            <Globe className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold">{meta.domain}</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            SEO Audit Report {meta.completed_at && `\u2014 ${new Date(meta.completed_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Key Metrics */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <MetricCard
            icon={<FileText className="h-5 w-5" />}
            label="Pages Crawled"
            value={meta.pages_crawled?.toString() ?? '0'}
            loading={metaLoading}
          />
          <MetricCard
            icon={<BarChart3 className="h-5 w-5" />}
            label="Avg SEO Score"
            value={meta.avg_seo_score ? `${meta.avg_seo_score}` : 'N/A'}
            valueClass={reportScoreColor(meta.avg_seo_score)}
            loading={metaLoading}
          />
          <MetricCard
            icon={<Clock className="h-5 w-5" />}
            label="Avg Response"
            value={overview?.avg_response_time ? `${Math.round(overview.avg_response_time)}ms` : 'N/A'}
            loading={overviewLoading}
          />
          <MetricCard
            icon={<CheckCircle2 className="h-5 w-5" />}
            label="Status 2xx"
            value={overview ? `${overview.status_2xx}` : '0'}
            loading={overviewLoading}
          />
        </div>

        {/* Score Distribution */}
        {scoreDist && totalScored > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Score Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 h-8 rounded-md overflow-hidden">
                {scoreDist.good > 0 && (
                  <div
                    className="bg-green-500/20 flex items-center justify-center text-xs font-medium text-green-700 dark:text-green-300"
                    style={{ flex: scoreDist.good }}
                    title={`${scoreDist.good} pages scoring 80+`}
                  >
                    {scoreDist.good} Good
                  </div>
                )}
                {scoreDist.ok > 0 && (
                  <div
                    className="bg-yellow-500/20 flex items-center justify-center text-xs font-medium text-yellow-700 dark:text-yellow-300"
                    style={{ flex: scoreDist.ok }}
                    title={`${scoreDist.ok} pages scoring 50-79`}
                  >
                    {scoreDist.ok} OK
                  </div>
                )}
                {scoreDist.poor > 0 && (
                  <div
                    className="bg-red-500/20 flex items-center justify-center text-xs font-medium text-red-700 dark:text-red-300"
                    style={{ flex: scoreDist.poor }}
                    title={`${scoreDist.poor} pages scoring below 50`}
                  >
                    {scoreDist.poor} Poor
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Priority Actions */}
        {actionsData && actionsData.actions.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Wrench className="h-4 w-4 text-primary" />
                <CardTitle className="text-base">Priority Actions</CardTitle>
                <Badge variant="secondary">{actionsData.actions.length}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {actionsData.actions.map((action, idx) => (
                  <div key={action.type} className="border rounded-md p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">
                        {idx + 1}. {action.label}
                      </span>
                      <div className="flex items-center gap-2">
                        <Badge variant={action.severity === 'error' ? 'destructive' : 'secondary'} className="text-[10px]">
                          {action.severity}
                        </Badge>
                        <Badge variant="outline">{action.count} pages</Badge>
                      </div>
                    </div>
                    {action.howToFix && (
                      <p className="text-xs text-muted-foreground mt-1">
                        <span className="font-medium text-foreground">How to fix: </span>
                        {action.howToFix}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Status Codes Summary */}
        {overview && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Response Status Codes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatusCodeBadge label="2xx Success" count={overview.status_2xx} color="bg-green-500/20 text-green-700 dark:text-green-300" />
                <StatusCodeBadge label="3xx Redirect" count={overview.status_3xx} color="bg-blue-500/20 text-blue-700 dark:text-blue-300" />
                <StatusCodeBadge label="4xx Client Error" count={overview.status_4xx} color="bg-yellow-500/20 text-yellow-700 dark:text-yellow-300" />
                <StatusCodeBadge label="5xx Server Error" count={overview.status_5xx} color="bg-red-500/20 text-red-700 dark:text-red-300" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Worst Pages */}
        {worstPagesData && worstPagesData.pages.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Lowest Scoring Pages</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2 text-xs font-medium text-muted-foreground">URL</th>
                      <th className="text-left py-2 px-2 text-xs font-medium text-muted-foreground">Title</th>
                      <th className="text-center py-2 px-2 text-xs font-medium text-muted-foreground">Status</th>
                      <th className="text-right py-2 px-2 text-xs font-medium text-muted-foreground">Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {worstPagesData.pages.slice(0, 10).map((page) => (
                      <tr key={page.url} className="border-b last:border-0">
                        <td className="py-2 px-2 max-w-[300px] truncate font-mono text-xs" title={page.url}>
                          {page.url}
                        </td>
                        <td className="py-2 px-2 max-w-[200px] truncate text-muted-foreground">
                          {page.title || '\u2014'}
                        </td>
                        <td className="py-2 px-2 text-center">{page.status_code}</td>
                        <td className="py-2 px-2 text-right">
                          <span className={`font-bold ${scoreColor(page.seo_score)}`}>
                            {Math.round(page.seo_score)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Issues */}
        {issuesLoading ? (
          <Skeleton className="h-48 w-full" />
        ) : issuesData && issuesData.issues.length > 0 ? (
          <>
            {errors.length > 0 && (
              <IssueGroup title="Errors" severity="error" issues={errors} icon={severityIcon} />
            )}
            {warnings.length > 0 && (
              <IssueGroup title="Warnings" severity="warning" issues={warnings} icon={severityIcon} />
            )}
            {infos.length > 0 && (
              <IssueGroup title="Notices" severity="info" issues={infos} icon={severityIcon} />
            )}
          </>
        ) : (
          <Card>
            <CardContent className="pt-6 text-center">
              <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No issues detected. This site is in great shape.</p>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center py-8 border-t">
          <p className="text-xs text-muted-foreground">
            Powered by{' '}
            <a href="https://whispering.au" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              Whispering Wombat
            </a>
            {' '}&mdash; SEO Audit Tool by{' '}
            <a href="https://jezweb.com.au" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              Jezweb
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}

function MetricCard({
  icon,
  label,
  value,
  valueClass,
  loading,
}: {
  icon: React.ReactNode
  label: string
  value: string
  valueClass?: string
  loading: boolean
}) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3 px-4">
        <div className="flex items-center gap-2 text-muted-foreground mb-1">
          {icon}
          <span className="text-xs">{label}</span>
        </div>
        {loading ? (
          <Skeleton className="h-7 w-16" />
        ) : (
          <div className={`text-2xl font-bold ${valueClass ?? ''}`}>{value}</div>
        )}
      </CardContent>
    </Card>
  )
}

function StatusCodeBadge({ label, count, color }: { label: string; count: number; color: string }) {
  return (
    <div className={`rounded-md p-3 ${color}`}>
      <div className="text-lg font-bold">{count}</div>
      <div className="text-xs">{label}</div>
    </div>
  )
}

function IssueGroup({
  title,
  severity,
  issues,
  icon,
}: {
  title: string
  severity: string
  issues: ReportIssue[]
  icon: (severity: string) => React.ReactNode
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          {icon(severity)}
          <CardTitle className="text-base">{title}</CardTitle>
          <Badge variant={severity === 'error' ? 'destructive' : 'secondary'}>
            {issues.length}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {issues.map((issue) => {
            const desc = issueDescriptions[issue.type]
            return (
              <div key={issue.type} className="border rounded-md p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">{issue.label}</span>
                  <Badge variant="outline">{issue.count} pages</Badge>
                </div>
                {desc && (
                  <div className="space-y-1 mt-2">
                    <p className="text-xs text-muted-foreground">{desc.description}</p>
                    <p className="text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">How to fix: </span>
                      {desc.howToFix}
                    </p>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
